// frontend/src/pages/SuperAdmin.jsx
// Page superadmin de démarchage — accès par mot de passe localStorage.
// Styles inline uniquement, aucune dépendance CSS du projet.
import { useState, useEffect } from "react";

const SUPERADMIN_PASSWORD = process.env.REACT_APP_SUPERADMIN_PASSWORD;
const ADMIN_SECRET_KEY = process.env.REACT_APP_ADMIN_SECRET_KEY;

// ── Styles réutilisables ─────────────────────────────────────────────────────

const s = {
  page: {
    minHeight: "100vh",
    background: "#f9fafb",
    fontFamily: "system-ui, -apple-system, sans-serif",
    color: "#111827",
    padding: "32px 24px"
  },
  card: {
    background: "white",
    borderRadius: "8px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.09)",
    padding: "24px"
  },
  input: {
    width: "100%",
    padding: "9px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "14px",
    boxSizing: "border-box",
    outline: "none"
  },
  label: {
    display: "block",
    fontSize: "12px",
    fontWeight: 600,
    color: "#374151",
    marginBottom: "6px"
  },
  btnPrimary: {
    padding: "9px 20px",
    background: "#1e293b",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    cursor: "pointer",
    fontWeight: 500
  },
  btnGreen: {
    padding: "8px 16px",
    background: "#16a34a",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    cursor: "pointer",
    fontWeight: 500
  },
  btnGhost: {
    background: "none",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    padding: "6px 12px",
    fontSize: "13px",
    cursor: "pointer",
    color: "#6b7280"
  },
  stat: {
    background: "#f8fafc",
    borderRadius: "6px",
    padding: "16px"
  },
  statValue: {
    fontSize: "28px",
    fontWeight: 700,
    color: "#1e293b"
  },
  statLabel: {
    fontSize: "12px",
    color: "#64748b",
    marginTop: "4px"
  },
  th: {
    textAlign: "left",
    padding: "8px 12px",
    color: "#6b7280",
    fontWeight: 600,
    fontSize: "12px"
  },
  td: {
    padding: "8px 12px",
    fontSize: "13px"
  }
};

// ── Composant mot de passe ───────────────────────────────────────────────────

function PasswordGate({ onSuccess }) {
  const [input, setInput] = useState("");
  const [error, setError] = useState("");

  function handleSubmit(e) {
    e.preventDefault();
    if (input === SUPERADMIN_PASSWORD) {
      localStorage.setItem("superadmin", "true");
      onSuccess();
    } else {
      setError("Accès refusé");
    }
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f9fafb", fontFamily: "system-ui, sans-serif" }}>
      <form onSubmit={handleSubmit} style={{ ...s.card, width: "320px" }}>
        <h2 style={{ margin: "0 0 24px", fontSize: "18px", fontWeight: 700 }}>Accès superadmin</h2>
        <label style={s.label}>Mot de passe</label>
        <input
          type="password"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="••••••••"
          autoFocus
          style={{ ...s.input, marginBottom: "12px" }}
        />
        {error && (
          <p style={{ color: "#dc2626", fontSize: "14px", margin: "0 0 12px" }}>{error}</p>
        )}
        <button type="submit" style={{ ...s.btnPrimary, width: "100%" }}>
          Valider
        </button>
      </form>
    </div>
  );
}

// ── Composant principal ──────────────────────────────────────────────────────

export default function SuperAdmin() {
  const [authed, setAuthed] = useState(localStorage.getItem("superadmin") === "true");

  // ── Démarchage ──
  const [label, setLabel] = useState("");
  const [from, setFrom]   = useState("");
  const [to, setTo]       = useState("");
  const [loading, setLoading] = useState(false);
  const [report, setReport]   = useState(null);
  const [error, setError]     = useState("");

  // ── Navigation ──
  const [activeSection, setActiveSection] = useState("demarchage");

  // ── Config ──
  const [maintenanceBanner, setMaintenanceBanner] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);

  useEffect(() => {
    fetch("/api/admin/config", { headers: { "x-admin-key": ADMIN_SECRET_KEY || "" } })
      .then(r => r.json())
      .then(data => setMaintenanceBanner(!!data.maintenance_banner))
      .catch(() => {});
  }, []);

  const toggleMaintenanceBanner = async () => {
    setConfigLoading(true);
    try {
      const res = await fetch("/api/admin/config", {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "x-admin-key": ADMIN_SECRET_KEY || "" },
        body: JSON.stringify({ maintenance_banner: !maintenanceBanner }),
      });
      const data = await res.json();
      setMaintenanceBanner(!!data.maintenance_banner);
    } catch (err) {
      console.error("❌ toggleMaintenanceBanner :", err);
    } finally {
      setConfigLoading(false);
    }
  };

  // ── Magasins ──
  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);

  // ── Feedbacks ──
  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbacksLoading, setFeedbacksLoading] = useState(false);

  // ── Vendeurs ──
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);

  if (!authed) {
    return <PasswordGate onSuccess={() => setAuthed(true)} />;
  }

  // ── Génération du rapport ──────────────────────────────────────────────────

  async function handleGenerateReport(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    setReport(null);

    try {
      const params = new URLSearchParams({ label });
      if (from) params.append("from", from);
      if (to)   params.append("to", to);

      const res = await fetch(`/api/admin/report?${params.toString()}`, {
        headers: { "x-admin-key": ADMIN_SECRET_KEY || "" }
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `HTTP ${res.status}`);
      }

      setReport(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // ── Export CSV ─────────────────────────────────────────────────────────────

  function handleExportCSV() {
    if (!report) return;

    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

    const rows = [
      ["Lieu détecté", "Total scans", "Clients uniques", "Pic d'activité"],
      [
        report.detected_label,
        report.total_scans,
        report.unique_users,
        report.peak ? `${report.peak.slot} (${report.peak.count} scans)` : "—"
      ],
      [],
      ["EAN", "Nombre de scans"],
      ...report.eans.map((e) => [e.barcode, e.count])
    ];

    const csv = rows
      .map((r) => r.map(escape).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `rapport-${label.replace(/\s+/g, "-")}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Magasins ───────────────────────────────────────────────────────────────

  async function fetchFeedbacks() {
    setFeedbacksLoading(true);
    try {
      const res = await fetch("/api/feedback", {
        headers: { "x-admin-key": ADMIN_SECRET_KEY || "" }
      });
      const data = await res.json();
      setFeedbacks(data.feedbacks || []);
    } catch (err) {
      console.error("Erreur fetchFeedbacks :", err);
    } finally {
      setFeedbacksLoading(false);
    }
  }

  async function fetchStores() {
    setStoresLoading(true);
    try {
      const res = await fetch("/api/admin/stores", {
        headers: { "x-admin-key": ADMIN_SECRET_KEY || "" }
      });
      const data = await res.json();
      setStores(data.stores || []);
    } catch (err) {
      console.error("Erreur fetchStores :", err);
    } finally {
      setStoresLoading(false);
    }
  }

  async function handleStoreAction(storeId, action, note = "") {
    try {
      const res = await fetch(`/api/admin/stores/${storeId}/verify`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "x-admin-key": ADMIN_SECRET_KEY || ""
        },
        body: JSON.stringify({ action, note })
      });
      if (res.ok) await fetchStores();
    } catch (err) {
      console.error("Erreur handleStoreAction :", err);
    }
  }

  // ── Vendeurs ───────────────────────────────────────────────────────────────

  async function fetchVendors() {
    setVendorsLoading(true);
    try {
      const res = await fetch("/api/admin/vendors", {
        headers: { "x-admin-key": ADMIN_SECRET_KEY || "" }
      });
      const data = await res.json();
      setVendors(data.vendors || []);
    } catch (err) {
      console.error("Erreur fetchVendors :", err);
    } finally {
      setVendorsLoading(false);
    }
  }

  async function handleVendorValidation(vendorId, validated) {
    try {
      const res = await fetch(`/api/admin/vendors/${vendorId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "x-admin-key": ADMIN_SECRET_KEY || ""
        },
        body: JSON.stringify({ superadmin_validated: validated })
      });
      if (res.ok) await fetchVendors();
    } catch (err) {
      console.error("Erreur handleVendorValidation :", err);
    }
  }

  // ── Rendu ──────────────────────────────────────────────────────────────────

  return (
    <div style={s.page}>
      <div style={{ maxWidth: "900px", margin: "0 auto" }}>

        {/* En-tête */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "28px" }}>
          <h1 style={{ fontSize: "22px", fontWeight: 700, margin: 0 }}>Outil de démarchage</h1>
          <button
            style={s.btnGhost}
            onClick={() => { localStorage.removeItem("superadmin"); setAuthed(false); }}
          >
            Déconnexion
          </button>
        </div>

        {/* Navigation */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "24px" }}>
          <button
            style={{ ...s.btnPrimary, opacity: activeSection === "demarchage" ? 1 : 0.5 }}
            onClick={() => setActiveSection("demarchage")}
          >
            Démarchage
          </button>
          <button
            style={{ ...s.btnPrimary, opacity: activeSection === "magasins" ? 1 : 0.5 }}
            onClick={() => { setActiveSection("magasins"); fetchStores(); }}
          >
            Magasins ({stores.length})
          </button>
          <button
            style={{ ...s.btnPrimary, opacity: activeSection === "feedbacks" ? 1 : 0.5 }}
            onClick={() => { setActiveSection("feedbacks"); fetchFeedbacks(); }}
          >
            Feedbacks ({feedbacks.length})
          </button>
          <button
            style={{ ...s.btnPrimary, opacity: activeSection === "vendeurs" ? 1 : 0.5 }}
            onClick={() => { setActiveSection("vendeurs"); fetchVendors(); }}
          >
            Vendeurs ({vendors.length})
          </button>
          <button
            style={{ ...s.btnPrimary, opacity: activeSection === "config" ? 1 : 0.5 }}
            onClick={() => setActiveSection("config")}
          >
            Config
          </button>
        </div>

        {/* ── Section démarchage ── */}
        {activeSection === "demarchage" && (
          <>
            {/* Formulaire de recherche */}
            <form onSubmit={handleGenerateReport} style={{ ...s.card, marginBottom: "20px" }}>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "flex-end" }}>

                <div style={{ flex: "2 1 200px" }}>
                  <label style={s.label}>Lieu (location_label)</label>
                  <input
                    type="text"
                    value={label}
                    onChange={e => setLabel(e.target.value)}
                    placeholder="ex : Zara, Kiabi, Avenue Voltaire"
                    required
                    style={s.input}
                  />
                </div>

                <div style={{ flex: "1 1 140px" }}>
                  <label style={s.label}>Date début</label>
                  <input
                    type="date"
                    value={from}
                    onChange={e => setFrom(e.target.value)}
                    style={s.input}
                  />
                </div>

                <div style={{ flex: "1 1 140px" }}>
                  <label style={s.label}>Date fin</label>
                  <input
                    type="date"
                    value={to}
                    onChange={e => setTo(e.target.value)}
                    style={s.input}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{ ...s.btnPrimary, opacity: loading ? 0.65 : 1, cursor: loading ? "not-allowed" : "pointer" }}
                >
                  {loading ? "Chargement…" : "Générer le rapport"}
                </button>
              </div>
            </form>

            {/* Erreur */}
            {error && (
              <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "6px", padding: "12px 16px", color: "#dc2626", fontSize: "14px", marginBottom: "16px" }}>
                {error}
              </div>
            )}

            {/* Rapport */}
            {report && (
              <div style={s.card}>

                {/* Titre + export */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px" }}>
                  <div>
                    <h2 style={{ fontSize: "17px", fontWeight: 700, margin: "0 0 4px" }}>
                      {report.detected_label}
                    </h2>
                    <p style={{ fontSize: "13px", color: "#6b7280", margin: 0 }}>
                      {from || to
                        ? `Période : ${from || "—"} → ${to || "—"}`
                        : "Toutes périodes confondues"}
                    </p>
                  </div>
                  <button style={s.btnGreen} onClick={handleExportCSV}>
                    Exporter CSV
                  </button>
                </div>

                {/* Stats */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "16px", marginBottom: "28px" }}>
                  <div style={s.stat}>
                    <div style={s.statValue}>{report.total_scans}</div>
                    <div style={s.statLabel}>Scans totaux</div>
                  </div>
                  <div style={s.stat}>
                    <div style={s.statValue}>{report.unique_users}</div>
                    <div style={s.statLabel}>Clients uniques</div>
                  </div>
                  <div style={s.stat}>
                    <div style={{ ...s.statValue, fontSize: report.peak ? "15px" : "28px", lineHeight: 1.3 }}>
                      {report.peak ? report.peak.slot : "—"}
                    </div>
                    <div style={s.statLabel}>
                      Pic d'activité{report.peak ? ` (${report.peak.count} scans)` : ""}
                    </div>
                  </div>
                </div>

                {/* Tableau EAN */}
                {report.eans.length > 0 ? (
                  <>
                    <h3 style={{ fontSize: "13px", fontWeight: 600, color: "#374151", margin: "0 0 12px", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      EAN scannés ({report.eans.length})
                    </h3>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                      <thead>
                        <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                          <th style={{ ...s.th, width: "40px" }}>#</th>
                          <th style={s.th}>EAN / Barcode</th>
                          <th style={{ ...s.th, textAlign: "right" }}>Scans</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.eans.map((e, i) => (
                          <tr
                            key={e.barcode}
                            style={{ borderBottom: "1px solid #f1f5f9", background: i % 2 === 0 ? "white" : "#fafafa" }}
                          >
                            <td style={{ ...s.td, color: "#9ca3af" }}>{i + 1}</td>
                            <td style={{ ...s.td, fontFamily: "monospace", color: "#1e293b" }}>{e.barcode}</td>
                            <td style={{ ...s.td, textAlign: "right", fontWeight: 600 }}>{e.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                ) : (
                  <p style={{ fontSize: "13px", color: "#9ca3af", textAlign: "center", padding: "24px 0", margin: 0 }}>
                    Aucun EAN scanné sur cette période
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {/* ── Section magasins ── */}
        {activeSection === "magasins" && (
          <div style={s.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <h2 style={{ fontSize: "17px", fontWeight: 700, margin: 0 }}>Magasins enregistrés</h2>
              <button style={s.btnGhost} onClick={fetchStores}>🔄 Actualiser</button>
            </div>
            <details style={{ marginBottom: "16px", background: "#f8fafc", borderRadius: "8px", padding: "12px 16px", border: "1px solid #e2e8f0" }}>
              <summary style={{ cursor: "pointer", fontWeight: 600, fontSize: "13px", color: "#374151" }}>
                📋 Guide de validation des magasins
              </summary>
              <div style={{ marginTop: "12px", fontSize: "12px", color: "#475569", lineHeight: 1.7 }}>
                <p style={{ margin: "0 0 8px", fontWeight: 700, color: "#1e293b" }}>🥉 Bronze — Validation manuelle (MVP actuel)</p>
                <p style={{ margin: "0 0 12px" }}>Vérifier le SIRET sur <a href="https://annuaire-entreprises.data.gouv.fr" target="_blank" rel="noopener noreferrer" style={{ color: "#4f46e5" }}>annuaire-entreprises.data.gouv.fr</a>. Confirmer que le nom du magasin correspond au nom légal. Cliquer "Valider" → statut "vérifié manuellement".</p>
                <p style={{ margin: "0 0 8px", fontWeight: 700, color: "#1e293b" }}>🥈 Silver — Email pro OTP (v1.1)</p>
                <p style={{ margin: "0 0 12px" }}>Le vendeur saisit un email avec domaine professionnel (ex: contact@magasin.fr). Un code OTP est envoyé via Brevo. Si validé → statut "vérifié email pro" automatiquement.</p>
                <p style={{ margin: "0 0 8px", fontWeight: 700, color: "#1e293b" }}>🥇 Gold — Photo live (v1.2)</p>
                <p style={{ margin: "0 0 12px" }}>Le vendeur envoie une photo de la devanture avec une feuille manuscrite "CRUSH NOW + code + date". Validation manuelle → statut "vérifié Gold".</p>
                <p style={{ margin: "0 0 8px", fontWeight: 700, color: "#1e293b" }}>🚫 Suspendre un magasin</p>
                <p style={{ margin: 0 }}>En cas de fraude détectée ou signalement. Le vendeur ne peut plus lier de produits. Contacter l'équipe avant toute suspension définitive.</p>
              </div>
            </details>
            {storesLoading ? (
              <p style={{ color: "#6b7280", fontSize: "14px" }}>Chargement…</p>
            ) : stores.length === 0 ? (
              <p style={{ color: "#9ca3af", fontSize: "14px", textAlign: "center", padding: "24px 0" }}>Aucun magasin enregistré</p>
            ) : (
              <div>
                {stores.map((store) => (
                  <div key={store._id} style={{ background: "#f8fafc", borderRadius: "8px", padding: "16px", border: "1px solid #e2e8f0", marginBottom: "12px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px", flexWrap: "wrap", gap: "8px" }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: "14px", color: "#1e293b" }}>{store.name}</div>
                        <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "2px" }}>{store.address}</div>
                      </div>
                      <span style={{
                        padding: "3px 8px", borderRadius: "999px", fontSize: "11px", fontWeight: 600, whiteSpace: "nowrap",
                        background: store.verified ? "#dcfce7" : store.verificationStatus === "suspendu" ? "#fee2e2" : "#fef9c3",
                        color: store.verified ? "#166534" : store.verificationStatus === "suspendu" ? "#991b1b" : "#854d0e"
                      }}>
                        {store.verificationStatus || (store.verified ? "vérifié manuellement" : "non vérifié")}
                      </span>
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "10px" }}>
                      <div>👤 {store.owner_id?.name || "—"} {store.owner_id?.email ? `· ${store.owner_id.email}` : ""}</div>
                      <div style={{ fontFamily: "monospace", marginTop: "2px" }}>SIRET : {store.siret || "—"}</div>
                    </div>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                      {!store.verified && store.verificationStatus !== "suspendu" && (
                        <button style={{ ...s.btnGreen, fontSize: "12px", padding: "5px 10px" }}
                          onClick={() => handleStoreAction(store._id, "verify")}>
                          ✅ Valider
                        </button>
                      )}
                      {store.verified && (
                        <button style={{ ...s.btnGhost, fontSize: "12px", padding: "5px 10px", color: "#dc2626", borderColor: "#dc2626" }}
                          onClick={() => handleStoreAction(store._id, "suspend", "Suspendu par admin")}>
                          🚫 Suspendre
                        </button>
                      )}
                      {store.verificationStatus === "suspendu" && (
                        <button style={{ ...s.btnGhost, fontSize: "12px", padding: "5px 10px" }}
                          onClick={() => handleStoreAction(store._id, "reset")}>
                          🔄 Réinitialiser
                        </button>
                      )}
                      <a href={`https://annuaire-entreprises.data.gouv.fr/etablissement/${store.siret}`}
                        target="_blank" rel="noopener noreferrer"
                        style={{ ...s.btnGhost, fontSize: "12px", padding: "5px 10px", textDecoration: "none" }}>
                        🔍 SIRET
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Section feedbacks ── */}
        {activeSection === "feedbacks" && (
          <div style={s.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <h2 style={{ fontSize: "17px", fontWeight: 700, margin: 0 }}>Feedbacks reçus</h2>
              <button style={s.btnGhost} onClick={fetchFeedbacks}>🔄 Actualiser</button>
            </div>
            {feedbacksLoading ? (
              <p style={{ color: "#6b7280", fontSize: "14px" }}>Chargement…</p>
            ) : feedbacks.length === 0 ? (
              <p style={{ color: "#9ca3af", fontSize: "14px", textAlign: "center", padding: "24px 0" }}>Aucun feedback reçu</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {feedbacks.map((fb) => (
                  <div key={fb._id} style={{ background: "#f8fafc", borderRadius: "8px", padding: "16px", border: "1px solid #e2e8f0" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px", flexWrap: "wrap", gap: "8px" }}>
                      <div>
                        <span style={{ fontWeight: 700, fontSize: "14px", color: "#1e293b" }}>{fb.title}</span>
                        <span style={{ marginLeft: "8px", fontSize: "11px", background: "#e0e7ff", color: "#3730a3", padding: "2px 8px", borderRadius: "999px", fontWeight: 600 }}>{fb.type}</span>
                        {fb.rating && <span style={{ marginLeft: "6px", fontSize: "12px", color: "#f59e0b" }}>{"★".repeat(fb.rating)}{"☆".repeat(5 - fb.rating)}</span>}
                      </div>
                      <span style={{ fontSize: "11px", color: "#94a3b8" }}>{new Date(fb.createdAt).toLocaleString("fr-FR")}</span>
                    </div>
                    <p style={{ fontSize: "13px", color: "#475569", margin: "0 0 8px", lineHeight: 1.5 }}>{fb.message}</p>
                    <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", fontSize: "11px", color: "#94a3b8" }}>
                      {fb.userRole && <span>👤 {fb.userRole}</span>}
                      {fb.email && <span>📧 {fb.email}</span>}
                      {fb.appVersion && <span>📱 v{fb.appVersion}</span>}
                      {fb.device && <span style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>🖥️ {fb.device}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Section vendeurs ── */}
        {activeSection === "vendeurs" && (
          <div style={s.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <h2 style={{ fontSize: "17px", fontWeight: 700, margin: 0 }}>Vendeurs ({vendors.length})</h2>
              <button style={s.btnGhost} onClick={fetchVendors}>🔄 Actualiser</button>
            </div>
            {vendorsLoading ? (
              <p style={{ color: "#6b7280", fontSize: "14px" }}>Chargement…</p>
            ) : vendors.length === 0 ? (
              <p style={{ color: "#9ca3af", fontSize: "14px", textAlign: "center", padding: "24px 0" }}>Aucun vendeur trouvé</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                    <th style={s.th}>Nom</th>
                    <th style={s.th}>Email</th>
                    <th style={s.th}>Inscription</th>
                    <th style={s.th}>Statut</th>
                    <th style={s.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vendors.map((v) => (
                    <tr key={v._id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={s.td}>{v.name || "—"}</td>
                      <td style={s.td}>{v.email || "—"}</td>
                      <td style={s.td}>{v.createdAt ? new Date(v.createdAt).toLocaleDateString("fr-FR") : "—"}</td>
                      <td style={s.td}>
                        {v.superadmin_validated ? (
                          <span style={{ background: "#dcfce7", color: "#166534", borderRadius: "999px", padding: "2px 10px", fontWeight: 700, fontSize: "12px" }}>✅ Validé</span>
                        ) : (
                          <span style={{ background: "#fee2e2", color: "#dc2626", borderRadius: "999px", padding: "2px 10px", fontWeight: 700, fontSize: "12px" }}>🚫 Non validé</span>
                        )}
                      </td>
                      <td style={{ ...s.td, display: "flex", gap: "6px" }}>
                        {!v.superadmin_validated && (
                          <button style={s.btnGreen} onClick={() => handleVendorValidation(v._id, true)}>
                            ✅ Valider
                          </button>
                        )}
                        {v.superadmin_validated && (
                          <button style={{ ...s.btnGhost, color: "#dc2626", borderColor: "#fca5a5" }} onClick={() => handleVendorValidation(v._id, false)}>
                            🚫 Révoquer
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── Section config ── */}
        {activeSection === "config" && (
          <div style={{ background: "white", borderRadius: "12px", padding: "24px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
            <h2 style={{ fontSize: "16px", fontWeight: 700, marginBottom: "20px" }}>⚙️ Configuration</h2>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0", borderBottom: "1px solid #f1f5f9" }}>
              <div>
                <div style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b" }}>Bannière maintenance</div>
                <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "2px" }}>Affiche un bandeau d'avertissement sur toute l'app</div>
              </div>
              <button
                onClick={toggleMaintenanceBanner}
                disabled={configLoading}
                style={{
                  padding: "8px 20px", borderRadius: "20px", border: "none", cursor: "pointer",
                  fontWeight: 700, fontSize: "13px",
                  background: maintenanceBanner ? "#16a34a" : "#e2e8f0",
                  color: maintenanceBanner ? "white" : "#475569",
                  opacity: configLoading ? 0.6 : 1,
                  transition: "all 0.2s",
                }}
              >
                {configLoading ? "..." : maintenanceBanner ? "ON" : "OFF"}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
